<template lang="pug">
div
  h1 Levels

  version-filter(
    key="versionFilter",
    @change="versionChanged",
    :disabled="this.loading"
  )

  environment-filter(
    key="environmentFilter",
    @change="environmentChanged",
    :disabled="this.loading"
  )

  button(@click="fetchAnalyticsData", v-if="!this.loading") Re-fetch Data
  span(v-else) Loading ...
  br
  br
  div(v-if="!analyticsData")
    br
    p Press 'Fetch Data' to retrieve analytics data (might take a while)
  div(v-else)
    table
      thead
        tr
          th Level
          th Completion count
          th Unlocked at
          th Belts
          th Buildings
          th Time to unlock
          th Desired Adjustment
          th % of players
          th Do not complete

      tbody
        tr(
          v-for="level in 100",
          v-if="p('level_' + level + '_time').count > 10",
          :key="level"
        )
          td {{ level }}
          td {{ formatBigNumber(p('level_' + level + '_time').count) }}
          td {{ formatSecondsShort(p('level_' + level + '_time').avg) }}
          td {{ formatBigNumber(p('level_' + level + '_belts').avg) }}
          td {{ formatBigNumber(p('level_' + level + '_buildings').avg) }}
          td {{ formatSecondsShort(p('level_' + level + '_time').avg - p('level_' + (level - 1) + '_time').avg) }}
          td {{ (getDesiredLevelTimeSeconds(level) / (p('level_' + level + '_time').avg - p('level_' + (level - 1) + '_time').avg) * 100.0 - 100.0).toFixed(2) }} %
          td {{ formatPercentage(p('level_' + level + '_time').count / p('game_start').count) }}
          td {{ formatPercentage(computeBounceRate(level)) }}

    h2 Level Details

    .stat
      strong Game starts
      span {{ p('game_start').count }}

    .levels
      div(
        v-for="level in 26",
        v-if="p('level_' + level + '_time').count > 25",
        :key="level"
      )
        h3 Level {{ level }}

        .stat
          strong Samples
          span {{ p('level_' + level + '_time').count }}

        .stat
          strong Time to complete
          span {{ formatSecondsShort(p('level_' + level + '_time').avg - p('level_' + (level - 1) + '_time').avg) }}

        .stat
          strong Game time at completion
          span {{ formatSecondsShort(p('level_' + level + '_time').avg) }}

        .stat
          strong % of all players
          span {{ formatPercentage(p('level_' + level + '_time').count / p('level_1_time').count) }}

        .stat
          strong % do not complete
          span {{ formatPercentage(computeBounceRate(level)) }}

        .stat(v-if="level >= 12")
          strong Blueprint Shapes
          span {{ formatBigNumber(p_nulled('level_' + level + '_shapes_CbCbCbRb:CwCwCwCw', 'level_' + level + '_time')) }}

        .stat
          strong Upgrades &gt; Belt
          span {{ Math.round(getUpgradeAtLevel('belt', level) * 100.0) / 100.0 }}

        .stat
          strong Upgrades &gt; Mining
          span {{ Math.round(getUpgradeAtLevel('miner', level) * 100.0) / 100.0 }}

        .stat
          strong Upgrades &gt; Processing
          span {{ Math.round(getUpgradeAtLevel('processors', level) * 100.0) / 100.0 }}

        .stat
          strong Upgrades &gt; Painting
          span {{ Math.round(getUpgradeAtLevel('painting', level) * 100.0) / 100.0 }}

        .stat
          strong Balancing &gt; Belt speed
          span {{ Math.round(getBeltSpeedAtLevel(level) * 100.0) / 100.0 }} items / s

        .stat
          strong Balancing &gt; Desired time
          span {{ formatSecondsShort(getDesiredLevelTimeSeconds(level)) }}


        .stat
          strong Balancing &gt; Suggested adjustment
          span {{ (getDesiredLevelTimeSeconds(level) / (p('level_' + level + '_time').avg - p('level_' + (level - 1) + '_time').avg) * 100.0 - 100.0).toFixed(2) }} %

        .stat
          strong Balancing &gt; Goal with 1 full belt (current)
          span {{ Math.round(getLevelThroughput(level)) }}

        .stat
          strong Balancing &gt; Goal
          span {{ counts[level] || '-' }}

        .stat
          strong Balancing &gt; Full belts placed
          span {{ Math.round(getLevelFullBelts(level) * 100) / 100 }}

        .stat
          strong Balancing &gt; New Goal
          span {{ Math.round(getNewGoal(level)) }}

        .stat
          strong Balancing &gt; New Goal (optimal)
          span {{ Math.round(getNewGoalOptimal(level)) }}

    h2 Upgrade Details
    div(v-for="upgradeId in upgrades")
      h3 {{ upgradeId }}

      div(v-for="level in 6")
        h4 Level {{ level }}
        .stat
          strong Time unlocked
          span {{ formatSecondsShort(p('upgrade_' + upgradeId + '@' + (level - 1) + '_time').avg) }}
        .stat
          strong Level unlocked (guess)
          span {{ reconstructLevelFromTime(p('upgrade_' + upgradeId + '@' + (level - 1) + '_time').avg) }}
        .stat
          strong Level unlocked goal
          span {{ formatBigNumber(getNewGoalOptimal(reconstructLevelFromTime(p('upgrade_' + upgradeId + '@' + (level - 1) + '_time').avg))) }}
</template>

<script>
// import Vue from "vue";
import ApiService from "@/services/api";
import KeyStatBox from "@/components/KeyStatBox";
import DateFilter from "@/components/DateFilter";
import VersionFilter from "@/components/VersionFilter";
import EnvironmentFilter from "@/components/EnvironmentFilter";

import { formatBigNumber, formatSecondsShort, formatPercentage } from "../utils";

export default {
    components: { KeyStatBox, DateFilter, VersionFilter, EnvironmentFilter },
    data: () => ({
        loading: false,
        version: null,
        environment: null,

        analyticsData: null,

        upgrades: ["belt", "miner", "processors", "painting"],

        beltSpeeds: [2, 4, 8, 12, 16, 20, 30],
        newBeltSpeeds: [2, 3, 4, 6, 8, 12, 16],
        counts: [
            0,
            40,
            40,
            100,
            120,
            200,
            400,
            800,
            1000,
            1400,
            1600,
            1800,
            2000,
            12000,
            16000,
            25000,
            50000,
            120000,
            250000,
        ],
    }),
    mounted() {},
    computed: {
        sqlVersionFilter() {
            if (this.version === "*") {
                return "true";
            }
            return "version = ~version";
        },

        sqlEnvironmentFilter() {
            if (this.environment === "*") {
                return "true";
            }
            return "environment = ~environment";
        },

        sqlFilters() {
            return this.sqlVersionFilter + " and " + this.sqlEnvironmentFilter;
        },
    },

    methods: {
        formatBigNumber,
        formatSecondsShort,
        formatPercentage,

        p(id) {
            return (
                this.analyticsData[id] || {
                    count: 0,
                    sum: 0,
                    avg: 0,
                }
            );
        },

        p_nulled(id, count_id) {
            const count = this.p(count_id).count;
            const sum = this.p(id).sum;
            return sum / Math.max(1, count);
        },

        getUpgradeAtLevel(id, level) {
            return this.p_nulled("level_" + level + "_upgrade_" + id, "level_" + level + "_time");
        },

        getEffectiveBeltSpeed(upgradeLevel, speeds = this.beltSpeeds) {
            const lower = Math.floor(upgradeLevel);
            const frac = upgradeLevel - lower;
            const upper = Math.ceil(upgradeLevel);

            return speeds[lower] * (1 - frac) + speeds[upper] * frac;
        },

        getDesiredLevelTimeSeconds(level) {
            if (this.environment === "steam") {
                return 90 * Math.pow(1.3, level);
            } else {
                return 150 * Math.pow(1.2, level);
            }
        },

        getBeltSpeedAtLevel(level, speeds = this.beltSpeeds) {
            return this.getEffectiveBeltSpeed(this.getUpgradeAtLevel("belt", level), speeds);
        },

        getLevelThroughput(level) {
            const timeToUnlock =
                this.p("level_" + level + "_time").avg - this.p("level_" + (level - 1) + "_time").avg;
            const beltSpeed = this.getBeltSpeedAtLevel(level);
            return timeToUnlock * beltSpeed;
        },

        reconstructLevelFromTime(time) {
            for (let i = 0; i < 30; ++i) {
                const unlockTime = this.p("level_" + i + "_time").avg;
                if (unlockTime >= time) {
                    return i;
                }
            }

            return -1;
        },

        getLevelFullBelts(level) {
            if (!this.counts[level]) {
                return 0;
            }
            const timeToUnlock =
                this.p("level_" + level + "_time").avg - this.p("level_" + (level - 1) + "_time").avg;
            const beltSpeed = this.getBeltSpeedAtLevel(level);

            const count = this.counts[level];

            const oneBelt = timeToUnlock * beltSpeed;
            return count / oneBelt;
        },

        getNewGoal(level) {
            const timeToUnlock =
                this.p("level_" + level + "_time").avg - this.p("level_" + (level - 1) + "_time").avg;
            const fullBelts = this.getLevelFullBelts(level);
            const beltSpeed = this.getBeltSpeedAtLevel(level, this.newBeltSpeeds);
            console.log(fullBelts, "*", beltSpeed, "*", timeToUnlock);
            return fullBelts * beltSpeed * timeToUnlock;
        },

        getNewGoalOptimal(level) {
            const timeToUnlock = this.getDesiredLevelTimeSeconds(level);
            const fullBelts = this.getLevelFullBelts(level);
            const beltSpeed = this.getBeltSpeedAtLevel(level, this.newBeltSpeeds);
            console.log(fullBelts, "*", beltSpeed, "*", timeToUnlock);
            return fullBelts * beltSpeed * timeToUnlock;
        },

        computeBounceRate(level) {
            let prevCount = this.p("level_" + (level - 1) + "_time").count;
            if (level <= 1) {
                prevCount = this.p("game_start").count;
            }
            const thisCount = this.p("level_" + level + "_time").count;
            return 1 - thisCount / Math.max(1, prevCount);
        },

        versionChanged(version) {
            console.log("Version changed:", version);
            this.version = version;
            this.fetchAnalyticsData();
        },

        environmentChanged(environment) {
            console.log("Environment changed:", environment);
            this.environment = environment;
            this.fetchAnalyticsData();
        },

        async fetchAnalyticsData() {
            this.analyticsData = null;

            if (!this.version || !this.environment) {
                return;
            }

            this.loading = true;

            const data = await ApiService.query(
                "select key, sample_count::float8, sample_sum::float8 from aggregated_stats where " +
                    this.sqlFilters,
                {
                    version: this.version,
                    environment: this.environment,
                }
            );

            const result = {};

            data.forEach(({ key, sample_count, sample_sum }) => {
                if (!result[key]) {
                    result[key] = {
                        count: sample_count,
                        sum: sample_sum,
                    };
                } else {
                    result[key].count += sample_count;
                    result[key].sum += sample_sum;
                }
            });

            for (const key in result) {
                result[key].avg = result[key].sum / Math.max(1, result[key].count);
            }
            this.analyticsData = result;

            this.loading = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.bounceRate {
    font-weight: 500;
    color: #f77;
    &.ok {
        color: rgb(248, 190, 54);
    }
    &.good {
        color: rgb(97, 221, 124);
    }
}

.stat {
    display: grid;
    grid-template-columns: 350px auto 1fr;
    margin-bottom: 5px;
    grid-column-gap: 25px;

    strong {
        font-weight: 300;
        color: #999;
    }

    > span {
        font-weight: 400;
        color: #444;
    }
}
</style>
